import { RoleAssignationFilter } from "@/components/common/section-components/filters/RoleAssignationFilter";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { Flex, useDisclosure } from "@chakra-ui/react";
import { lazy, Suspense, useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { StandardTable } from "@/components/table/StandardTable";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "@/constants/routes";
import { BOUser } from "./schema/role-assignation.schema";
import { useRoles } from "@/hooks/useRoles";
import { PermissionCode } from "@/constants/permissions";
import {
  clearFiltersHandler,
  fetchBOUsersHandler,
  handleDeleteAssignationExternal,
  submitFiltersHandler,
} from "./schema/handler-functions";
import { RoleAssignationActions } from "@/components/common/section-components/action/RoleAssignationActions";
import { NoData } from "@/components/common/section-components/NoData";
import { usePolling } from "@/hooks/usePolling";
import { isEmpty } from "@/utils/query-string";

export const RoleAssignationPage = () => {
  const navigate = useNavigate();
  const [BOUsers, setBOUsers] = useState<{
    listado: BOUser[];
    totalPages: number;
    totalRecords: number;
  }>({
    listado: [],
    totalPages: 1,
    totalRecords: 1,
  });
  const [isActive, setIsActive] = useState<boolean>(false);
  const [loadingBOUsers, setLoadingBOUsers] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);
  const { control, handleSubmit, setValue, getValues, watch } = useForm();

  const { userPermissions, fetchPermissions, loadingPermissions } = useRoles();

  const [selection, setSelection] = useState<string[]>([]);

  const fetchBOUsers = async (filters?: any, showLoad = true) => {
    await fetchBOUsersHandler({
      filters,
      showLoad,
      setBOUsers,
      setLoadingBOUsers,
      navigate,
      fetchPermissions,
      setCurrentFilters,
    });
  };

  useEffect(() => {
    setIsActive(true);
  }, []);

  useEffect(() => {
    if (isActive && !loadingPermissions) fetchBOUsers();
  }, [isActive, loadingPermissions]);

  usePolling({
    callback: () =>
      fetchBOUsers({ ...currentFilters, "P.Pagina": page }, false),
    delay: 10000,
    isActive,
  });

  const { open: openConfirmModal, setOpen: setOpenConfirmModal } =
    useDisclosure();

  const hasSelection = selection?.length > 0;
  const indeterminate =
    hasSelection && selection?.length < Number(BOUsers?.listado?.length);

  const [currentFilters, setCurrentFilters] = useState<{
    Usuario?: string;
    NombreApellido?: string;
    Roles?: number[];
  }>({
    Usuario: "",
    NombreApellido: "",
    Roles: [],
  });

  const headerElements = [
    {
      label: "Mail",
      value: "mail",
    },
    {
      label: "Nombre y Apellido",
      value: "nombreApellido",
    },
    {
      label: "Roles asignados",
      value: "roles",
    },
  ];

  const clearFilters = async () => {
    clearFiltersHandler({
      setValue,
      setFiltersApplied,
      submitFilters,
      getValues,
    });
  };

  const handlePageChange = async (page: number) => {
    setPage(page);
    await fetchBOUsers({ ...currentFilters, "P.Pagina": page });
  };

  const submitFilters = async (event: FieldValues) => {
    submitFiltersHandler({
      event,
      fetchBOUsers,
      setFiltersApplied,
      fetchPermissions,
      navigate,
      setOpenFilters,
      setCurrentFilters,
      setLoadingBOUsers,
    });
  };

  const handleAddAssignation = () => {
    navigate(AppRoutes.USER_ROLE_CREATION);
  };

  const handleEditAssignation = (id?: string) => {
    navigate(AppRoutes.USER_ROLE_EDIT.replace(":id", String(id)));
  };

  const [loadingDelete, setLoadingDelete] = useState<boolean>(false);

  const handleDeleteAssignation = async () => {
    await handleDeleteAssignationExternal({
      BOUsers,
      currentFilters,
      fetchBOUsers,
      fetchPermissions,
      selection,
      setLoadingDelete,
      setOpenConfirmModal,
      navigate,
    });
  };

  const watchedValues = watch();
  const isFormEmpty = Object.values(watchedValues).every(
    (val) => isEmpty(val)
  );

  const getActionElements = (item: BOUser) => (
    <RoleAssignationActions
      item={item}
      handleEditAssignation={handleEditAssignation}
    />
  );

  const hasWriteAccess = Boolean(
    userPermissions?.some(
      (p) =>
        p.codigo === PermissionCode.ROLE_ASSIGNATION && p.tipoPermiso === "W",
    ),
  );

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      gap="30px"
      data-testid="role-assignation-page"
    >
      <SectionHeader
        title="Asignación de roles y sucursales"
        addButtonLabel="Agregar"
        deleteButtonLabel="Eliminar"
        disableAdd={!hasWriteAccess}
        disableDelete={!selection?.length || !hasWriteAccess}
        handleAdd={handleAddAssignation}
        handleDelete={() => setOpenConfirmModal(true)}
        loading={loadingBOUsers}
        showButtons={hasWriteAccess}
      />

      <RoleAssignationFilter
        openFilters={openFilters}
        setOpenFilters={setOpenFilters}
        control={control}
        clearFilters={clearFilters}
        handleSubmit={handleSubmit}
        submitFilters={submitFilters}
        filtersApplied={filtersApplied}
        disableClear={isFormEmpty}
      />

      <Flex flexDir="column">
        {Boolean(!loadingBOUsers && !BOUsers?.listado?.length) && <NoData />}
        {Boolean(!loadingBOUsers && BOUsers?.listado?.length) && (
          <>
            <StandardTable
              rowId="usuario"
              indeterminate={indeterminate}
              items={
                BOUsers?.listado?.map((user) => ({
                  ...user,
                  roles:
                    user?.roles?.length > 3
                      ? user.roles
                          .map((el) => el?.nombre)
                          .slice(0, 3)
                          .join(", ") + ", ..."
                      : user?.roles?.map((el) => el?.nombre).join(", "),
                })) || []
              }
              selection={selection}
              setSelection={setSelection}
              headers={headerElements}
              noActions={!hasWriteAccess}
              noSelection={!hasWriteAccess}
              actionsHeaderLabel="Edición"
              actionElements={getActionElements}
            />
            <PaginationComponent
              page={page}
              handlePageChange={handlePageChange}
              pageSize={10}
              count={BOUsers?.totalRecords}
            />
          </>
        )}
      </Flex>
      <Suspense>
        <ConfirmModalDynamic
          openConfirm={openConfirmModal}
          setOpenConfirm={setOpenConfirmModal}
          handleAccept={handleDeleteAssignation}
          loadingAccept={loadingDelete}
          title="Eliminar asignación"
          subtitle="¿Está seguro de que desea realizar esta acción?"
        />
      </Suspense>
    </Flex>
  );
};

const ConfirmModalDynamic = lazy(() =>
  import("@/components/modal/ConfirmModal").then((el) => ({
    default: el["ConfirmModal"],
  })),
);
